import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Copy, ArrowClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";

const EMPTY = { material: "", site: "", quantity: "", unit: "pcs", condition: "", notes: "", is_active: true };

const CRM_BADGE = {
  sent: "bg-green-100 text-green-800 border-green-300",
  failed: "bg-red-100 text-red-800 border-red-300",
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  not_configured: "bg-neutral-100 text-neutral-700 border-neutral-300",
};

export default function AdminOldMaterialTab({ isAgent }) {
  const [items, setItems] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, e] = await Promise.all([api.get("/admin/old-material"), api.get("/admin/old-material/enquiries")]);
      setItems(s.data.items || []);
      setEnquiries(e.data.items || []);
    } catch {
      toast.error("Failed to load old material");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditingId(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({ ...EMPTY, ...item });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...form, quantity: Number(form.quantity) || 0 };
    try {
      if (editingId) await api.put(`/admin/old-material/${editingId}`, body);
      else await api.post("/admin/old-material", body);
      toast.success("Saved");
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail?.toString?.() || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete ${item.material} at ${item.site}?`)) return;
    try {
      await api.delete(`/admin/old-material/${item.id}`);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const resend = async (enq) => {
    try {
      await api.post(`/admin/old-material/enquiries/${enq.id}/resend`);
      toast.success("Resend queued");
      setTimeout(load, 2000);
    } catch {
      toast.error("Resend failed");
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/old-material`);
    toast.success("Buyer link copied");
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-6">
      <Card className="border-neutral-200 rounded-sm">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-bold">Old material stock</h2>
              <p className="text-xs text-neutral-500">Shown publicly at /old-material (active items with quantity &gt; 0).</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={copyLink} className="rounded-sm">
                <Copy size={14} className="mr-1" /> Copy buyer link
              </Button>
              <Button size="sm" onClick={openNew} className="rounded-sm bg-neutral-900" data-testid="old-material-add-btn">
                <Plus size={14} className="mr-1" /> Add item
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Site</TableHead>
                  <TableHead>Material</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Condition</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-neutral-500 py-6">No items yet</TableCell>
                  </TableRow>
                )}
                {items.map((it) => (
                  <TableRow key={it.id}>
                    <TableCell className="font-medium">{it.site}</TableCell>
                    <TableCell>{it.material}</TableCell>
                    <TableCell className="text-right">{it.quantity} {it.unit}</TableCell>
                    <TableCell>{it.condition || "-"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={it.is_active ? "border-green-300 text-green-800" : "text-neutral-500"}>
                        {it.is_active ? "Listed" : "Hidden"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(it)}><PencilSimple size={14} /></Button>
                      {!isAgent && (
                        <Button variant="ghost" size="sm" onClick={() => remove(it)}><Trash size={14} className="text-red-600" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card className="border-neutral-200 rounded-sm">
        <CardContent className="p-4 sm:p-6">
          <h2 className="text-lg font-bold mb-1">Buy enquiries</h2>
          <p className="text-xs text-neutral-500 mb-4">Each enquiry is forwarded to RealTex Scaffo India. Failed sends can be retried.</p>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Material</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Site</TableHead>
                  <TableHead>RealTex</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enquiries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-neutral-500 py-6">No enquiries yet</TableCell>
                  </TableRow>
                )}
                {enquiries.map((q) => (
                  <TableRow key={q.id}>
                    <TableCell className="whitespace-nowrap text-xs">{new Date(q.created_at).toLocaleString("en-IN")}</TableCell>
                    <TableCell>{q.name}</TableCell>
                    <TableCell><a href={`tel:${q.phone}`} className="underline">{q.phone}</a></TableCell>
                    <TableCell>{q.material}</TableCell>
                    <TableCell className="text-right">{q.quantity}</TableCell>
                    <TableCell>{q.site}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      <Badge variant="outline" className={CRM_BADGE[q.crm_status] || ""} title={q.crm_error || ""}>
                        {(q.crm_status || "pending").replace("_", " ")}
                      </Badge>
                      {q.crm_status !== "sent" && (
                        <Button variant="ghost" size="sm" onClick={() => resend(q)} title="Resend to RealTex">
                          <ArrowClockwise size={14} />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit item" : "Add old material"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label>Site</Label>
              <Input required value={form.site} onChange={set("site")} placeholder="e.g. Pune – Hinjewadi yard" />
            </div>
            <div>
              <Label>Material</Label>
              <Input required value={form.material} onChange={set("material")} placeholder="e.g. Cuplock vertical 3m" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Quantity</Label>
                <Input required type="number" min="0" step="any" value={form.quantity} onChange={set("quantity")} />
              </div>
              <div>
                <Label>Unit</Label>
                <Input value={form.unit} onChange={set("unit")} placeholder="pcs / kg / sets" />
              </div>
            </div>
            <div>
              <Label>Condition</Label>
              <Input value={form.condition} onChange={set("condition")} placeholder="e.g. Good, minor rust" />
            </div>
            <div>
              <Label>Internal notes</Label>
              <Input value={form.notes} onChange={set("notes")} placeholder="Not shown to buyers" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} />
              <Label>Show on public page</Label>
            </div>
            <Button type="submit" disabled={saving} className="w-full rounded-sm bg-neutral-900">
              {saving ? "Saving…" : "Save"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
